import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, House, Users, MapPin, Hammer, Sparkle, Star } from "@phosphor-icons/react";
import { HERO_IMG, serviceImage } from "../../lib/api";
import { useContent, pick } from "../../lib/content";
import { useSite } from "../../lib/site";
import { useServices, useTestimonials } from "../../lib/services";
import SEO from "../../components/SEO";
import CTABand from "../../components/public/CTABand";

const ICONS = { ShieldCheck, House, Users, MapPin, Hammer, Sparkle };

export default function Home() {
  const c = useContent();
  const site = useSite();
  const services = useServices();
  const testimonials = useTestimonials();

  const featured = services.slice(0, 6);
  const stats = pick(c, "home.stats", []);
  const benefits = pick(c, "home.benefits", []);

  return (
    <div data-testid="home-page">
      <SEO
        path="/"
        title={null}
        description={`${site?.business_name || "ATC Home Pro's"} — home improvement & construction in ${site?.address || "Loganville, PA"}. Decks, kitchens, baths, additions, roofing & siding. Family-owned. Lifetime labor warranty.`}
        image={HERO_IMG}
      />
      {/* HERO */}
      <section className="relative bg-atc-black text-white overflow-hidden" data-testid="hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-atc-black via-atc-black/80 to-transparent" />
          <div className="absolute inset-0 bg-blueprint-grid-dark bg-[size:4rem_4rem] opacity-30" />
        </div>
        <div className="container-max relative min-h-[88vh] flex flex-col justify-end pb-16 pt-32">
          <div className="max-w-4xl">
            <div className="overline mb-5 text-atc-red">{(site?.address || "Loganville, PA").toUpperCase()} · {pick(c, "home.hero_overline_suffix", "Est. ATC Home Pro's")}</div>
            <h1 className="h-display">
              {pick(c, "home.hero_title_line_1", "When quality")}<br />
              {pick(c, "home.hero_title_line_2", "counts… count")}<br />
              <span className="text-atc-red">{pick(c, "home.hero_title_line_3_red", "on us.")}</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-white/80 max-w-2xl font-body">
              {pick(c, "home.hero_body", "")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-testid="hero-cta-primary">
                {pick(c, "home.hero_cta_primary", "Get a free estimate")} <ArrowUpRight size={18} weight="bold" />
              </Link>
              <Link to="/services" className="btn-ghost-white" data-testid="hero-cta-secondary">
                {pick(c, "home.hero_cta_secondary", "Explore services")}
              </Link>
            </div>
          </div>

          {stats.length > 0 && (
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
              {stats.map((s, i) => (
                <div key={i} className="bg-atc-black p-6">
                  <div className="font-display text-5xl md:text-6xl text-atc-red leading-none">{s.value}</div>
                  <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* INTRO */}
      <section className="section-pad bg-atc-paper relative" data-testid="intro">
        <div className="absolute inset-0 bg-blueprint-grid bg-[size:4rem_4rem] opacity-40 pointer-events-none" />
        <div className="container-max relative grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline mb-4">{pick(c, "home.intro_overline", "About ATC")}</div>
            <h2 className="h-section">{pick(c, "home.intro_title_line_1", "Houses into")}<br/>{pick(c, "home.intro_title_line_2", "dream homes.")}</h2>
            <div className="rule-red mt-6" />
          </div>
          <div className="md:col-span-8">
            <p className="text-lg leading-relaxed text-atc-charcoal">{pick(c, "home.intro_body_1", "")}</p>
            <p className="mt-5 text-lg leading-relaxed text-atc-charcoal">{pick(c, "home.intro_body_2", "")}</p>
            <div className="mt-8 flex flex-wrap gap-3 text-sm font-mono">
              {[...pick(c, "global.trust_badges", []), `Showroom in ${(site?.showroom_address || "Loganville, PA").split(",")[0].trim()}`].map((t) => (
                <span key={t} className="px-3 py-1 border border-atc-black text-atc-black uppercase tracking-widest">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      {benefits.length > 0 && (
        <section className="section-pad bg-white border-y-2 border-atc-black" data-testid="why-choose-us">
          <div className="container-max">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
              <div className="md:col-span-5">
                <div className="overline mb-4">{pick(c, "home.why_overline", "Why ATC")}</div>
                <h2 className="h-section">{pick(c, "home.why_title_line_1", "The difference")}<br/>{pick(c, "home.why_title_line_2", "is in the details.")}</h2>
              </div>
              <div className="md:col-span-7 flex items-end">
                <p className="text-atc-muted text-lg">{pick(c, "home.why_subtitle", "")}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-atc-black">
              {benefits.map((b, i) => {
                const Icon = ICONS[b.icon] || ShieldCheck;
                const lastRowMd = i >= benefits.length - (benefits.length % 2 || 2);
                const lastRowLg = i >= benefits.length - (benefits.length % 3 || 3);
                return (
                  <div
                    key={i}
                    className={`p-8 bg-white group hover:bg-atc-black hover:text-white transition-colors duration-300 border-atc-black
                      ${i !== benefits.length - 1 ? "border-b-2" : ""}
                      ${i % 2 !== 1 ? "md:border-r-2" : ""}
                      ${!lastRowMd ? "md:border-b-2" : "md:border-b-0"}
                      ${i % 3 !== 2 ? "lg:border-r-2" : "lg:border-r-0"}
                      ${!lastRowLg ? "lg:border-b-2" : "lg:border-b-0"}`}
                    data-testid={`benefit-${i}`}
                  >
                    <Icon size={40} weight="bold" className="text-atc-red" />
                    <h3 className="h-card mt-5">{b.title}</h3>
                    <p className="mt-3 text-sm text-atc-muted group-hover:text-white/70">{b.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* SERVICES BENTO */}
      <section className="section-pad bg-atc-paper" data-testid="services-preview">
        <div className="container-max">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="overline mb-4">{pick(c, "home.services_overline", "What we do")}</div>
              <h2 className="h-section">{pick(c, "home.services_title", "Popular services")}</h2>
            </div>
            <Link to="/services" className="btn-outline self-start" data-testid="all-services-link">
              {pick(c, "home.services_view_all", "View all services")} <ArrowUpRight size={18} weight="bold" />
            </Link>
          </div>

          {featured.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <Link
                to={`/services/${featured[0].slug}`}
                className="md:col-span-8 md:row-span-2 group relative block overflow-hidden border-2 border-atc-black bg-atc-black min-h-[420px]"
                data-testid="service-feature-main"
              >
                <img src={serviceImage(featured[0])} alt={featured[0].title} className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-atc-black via-atc-black/40 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-8 text-white">
                  <div className="overline text-atc-red mb-3">Featured</div>
                  <h3 className="font-display text-4xl md:text-5xl uppercase leading-none">{featured[0].title}</h3>
                  <p className="mt-3 max-w-md text-white/80">{featured[0].short_description}</p>
                  <div className="mt-5 inline-flex items-center gap-2 font-heading uppercase tracking-widest text-xs">
                    Learn more <ArrowUpRight size={14} weight="bold" />
                  </div>
                </div>
              </Link>

              {featured.slice(1, 5).map((svc) => (
                <Link
                  key={svc.id}
                  to={`/services/${svc.slug}`}
                  className="md:col-span-2 group relative block overflow-hidden border-2 border-atc-black bg-atc-black min-h-[200px]"
                  data-testid={`service-tile-${svc.slug}`}
                >
                  <img src={serviceImage(svc)} alt={svc.title} className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition duration-500 opacity-80" />
                  <div className="absolute inset-0 bg-atc-black/60 group-hover:bg-atc-black/30 transition-colors" />
                  <div className="relative h-full flex flex-col justify-end p-5 text-white">
                    <h3 className="font-display text-2xl uppercase leading-none">{svc.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SPECIALTIES */}
      {site?.specialties?.length > 0 && (
        <section className="section-pad bg-white border-y-2 border-atc-black" data-testid="specialties">
          <div className="container-max">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
              <div className="md:col-span-5">
                <div className="overline mb-4">{pick(c, "home.specialties_overline", "Full Capabilities")}</div>
                <h2 className="h-section">{pick(c, "home.specialties_title_line_1", "Everything")}<br/>{pick(c, "home.specialties_title_line_2", "under one roof.")}</h2>
                <div className="rule-red mt-6" />
              </div>
              <div className="md:col-span-7 flex items-end">
                <p className="text-atc-muted text-lg">{pick(c, "home.specialties_body", "")}</p>
              </div>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-atc-black">
              {site.specialties.map((s, i) => {
                const lastRowSm = i >= site.specialties.length - (site.specialties.length % 2 || 2);
                const lastRowLg = i >= site.specialties.length - (site.specialties.length % 3 || 3);
                return (
                  <li
                    key={i}
                    className={`group flex items-center gap-3 p-5 bg-white hover:bg-atc-black transition-colors duration-200 border-atc-black
                      ${i !== site.specialties.length - 1 ? "border-b-2" : ""}
                      ${i % 2 === 0 ? "sm:border-r-2 lg:border-r-0" : ""}
                      ${!lastRowSm ? "sm:border-b-2" : "sm:border-b-0"}
                      ${(i + 1) % 3 !== 0 ? "lg:border-r-2" : "lg:border-r-0"}
                      ${!lastRowLg ? "lg:border-b-2" : "lg:border-b-0"}`}
                    data-testid={`specialty-${i}`}
                  >
                    <span className="font-mono text-[10px] text-atc-red font-bold tracking-widest shrink-0 w-8">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-heading uppercase tracking-tight text-atc-black group-hover:text-white text-sm md:text-base leading-tight">{s}</span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-6 text-sm text-atc-muted italic">{pick(c, "home.specialties_footnote", "")}</p>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="section-pad bg-atc-black text-white relative overflow-hidden" data-testid="testimonials">
          <div className="absolute inset-0 bg-blueprint-grid-dark bg-[size:4rem_4rem] opacity-20" />
          <div className="container-max relative">
            <div className="overline mb-4">{pick(c, "home.testimonials_overline", "Word of mouth")}</div>
            <h2 className="h-section text-white mb-12">{pick(c, "home.testimonials_title_line_1", "Neighbors")}<br/>{pick(c, "home.testimonials_title_line_2", "who stayed loyal.")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-white/20">
              {testimonials.slice(0, 3).map((t, i, arr) => (
                <div
                  key={t.id}
                  className={`p-8 ${i !== arr.length - 1 ? "border-b border-white/20 md:border-b-0" : ""} ${i !== arr.length - 1 ? "md:border-r md:border-white/20" : ""}`}
                  data-testid={`testimonial-${i}`}
                >
                  <div className="flex gap-0.5 text-atc-red mb-5">
                    {Array.from({ length: t.rating || 5 }).map((_, j) => (
                      <Star key={j} size={16} weight="fill" />
                    ))}
                  </div>
                  <p className="text-lg leading-relaxed text-white/90">"{t.body}"</p>
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <div className="font-heading uppercase tracking-widest text-sm">{t.name}</div>
                    {t.location && <div className="font-mono text-xs text-white/50 mt-1">{t.location}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SERVICE AREAS */}
      <section className="section-pad bg-atc-paper" data-testid="service-areas">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="overline mb-4">{pick(c, "home.areas_overline", "Service area")}</div>
              <h2 className="h-section">{pick(c, "home.areas_title_line_1", "Proudly")}<br/>{pick(c, "home.areas_title_line_2", "local.")}</h2>
              <p className="mt-5 text-atc-muted text-lg">{pick(c, "home.areas_body", "")}</p>
            </div>
            <div className="md:col-span-7">
              <div className="flex flex-wrap gap-2">
                {(site?.service_areas || []).map((area) => (
                  <span key={area} className="px-4 py-2 bg-atc-black text-white font-heading uppercase tracking-widest text-xs" data-testid={`area-tag-${area.replace(/\s/g, "-").toLowerCase()}`}>
                    {area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABand phone={site?.phone} />
    </div>
  );
}
